import React from "react";
import PropTypes from "prop-types";
import { Button, IconButton, MenuItem, Select } from "@material-ui/core";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@material-ui/icons";
import { DatePicker, MuiPickersUtilsProvider } from "material-ui-pickers";
import MomentUtils from "@date-io/moment";
import "./ScheduleDateToolbar.css";

const MODE_OPTIONS = [
    { value: "month", label: "Month" },
    { value: "week", label: "Week" },
    { value: "day", label: "Day" },
    { value: "custom", label: "Custom range" }
];

const formatPeriod = (mode, anchorDate) => {
    if (!anchorDate || !anchorDate.isValid || !anchorDate.isValid()) return "";
    if (mode === "month") return anchorDate.format("MMMM YYYY");
    if (mode === "day") return anchorDate.format("ddd, MMM D, YYYY");

    const start = anchorDate.clone().startOf("isoWeek");
    const end = start.clone().endOf("isoWeek");
    if (start.isSame(end, "month")) {
        return `${start.format("MMM D")} – ${end.format("D, YYYY")}`;
    }
    if (start.isSame(end, "year")) {
        return `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`;
    }
    return `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`;
};

const ScheduleDateToolbar = ({
    mode,
    anchorDate,
    startDate,
    endDate,
    customStart,
    customEnd,
    onModeChange,
    onNavigate,
    onAnchorChange,
    onReturnToCurrent,
    onCustomStartChange,
    onCustomEndChange,
    onApplyCustom,
    customError,
    canApplyCustom,
    dayOfWeek,
    onDayOfWeekChange,
    showDayFilter,
    loading
}) => {
    const isCustom = mode === "custom";
    const dateViews = mode === "month" ? ["year", "month"] : ["year", "month", "day"];
    const dateFormat = mode === "month" ? "MMMM YYYY" : "ddd, MMM D, YYYY";

    return (
        <MuiPickersUtilsProvider utils={MomentUtils}>
            <div className={`schedule-date-toolbar${isCustom ? " schedule-date-toolbar--custom" : ""}`} aria-busy={loading}>
                <div className="schedule-date-toolbar__shortcuts" role="group" aria-label="Current date shortcuts">
                    {[
                        { mode: "day", label: "Today" },
                        { mode: "week", label: "This week" },
                        { mode: "month", label: "This month" }
                    ].map(shortcut => (
                        <Button
                            key={shortcut.mode}
                            className="schedule-date-toolbar__today"
                            onClick={() => onReturnToCurrent(shortcut.mode)}
                            size="small"
                        >
                            {shortcut.label}
                        </Button>
                    ))}
                </div>

                <Select
                    className="schedule-date-toolbar__mode"
                    value={mode}
                    onChange={event => onModeChange(event.target.value)}
                    inputProps={{ "aria-label": "Schedule view" }}
                    SelectDisplayProps={{ "aria-label": "Schedule view" }}
                >
                    {MODE_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>

                {showDayFilter && (
                    <Select
                        className="schedule-date-toolbar__mode schedule-date-toolbar__day"
                        value={dayOfWeek === null || dayOfWeek === undefined ? "" : dayOfWeek}
                        displayEmpty
                        onChange={event => {
                            const value = event.target.value;
                            onDayOfWeekChange(value === "" ? null : Number(value));
                        }}
                        inputProps={{ "aria-label": "Filter by day of week" }}
                        SelectDisplayProps={{ "aria-label": "Filter by day of week" }}
                    >
                        <MenuItem value="">All days</MenuItem>
                        <MenuItem value={1}>Monday</MenuItem>
                        <MenuItem value={2}>Tuesday</MenuItem>
                        <MenuItem value={3}>Wednesday</MenuItem>
                        <MenuItem value={4}>Thursday</MenuItem>
                        <MenuItem value={5}>Friday</MenuItem>
                        <MenuItem value={6}>Saturday</MenuItem>
                        <MenuItem value={0}>Sunday</MenuItem>
                    </Select>
                )}

                {!isCustom && (
                    <div className="schedule-date-toolbar__period">
                        <IconButton
                            className="schedule-date-toolbar__arrow"
                            aria-label="Go to previous period"
                            onClick={() => onNavigate(-1)}
                        >
                            <KeyboardArrowLeft />
                        </IconButton>
                        <DatePicker
                            value={anchorDate}
                            onChange={onAnchorChange}
                            format={dateFormat}
                            views={dateViews}
                            className="schedule-date-toolbar__period-picker"
                            InputProps={{ readOnly: true }}
                            labelFunc={date => formatPeriod(mode, date)}
                            inputProps={{ "aria-label": `Choose ${mode === "month" ? "month" : "date"}` }}
                            autoOk
                            aria-label={`Choose ${mode === "month" ? "month" : "date"}`}
                        />
                        <IconButton
                            className="schedule-date-toolbar__arrow"
                            aria-label="Go to next period"
                            onClick={() => onNavigate(1)}
                        >
                            <KeyboardArrowRight />
                        </IconButton>
                    </div>
                )}

                {isCustom && (
                    <div className="schedule-date-toolbar__custom">
                        <div className="schedule-date-toolbar__custom-controls">
                            <div className="schedule-date-toolbar__custom-field">
                                <label className="schedule-date-toolbar__custom-label" htmlFor="schedule-custom-from">
                                    From
                                </label>
                                <DatePicker
                                    id="schedule-custom-from"
                                    value={customStart}
                                    onChange={onCustomStartChange}
                                    format="DD-MM-YYYY"
                                    className="schedule-date-toolbar__custom-picker"
                                    autoOk
                                    clearable
                                    aria-label="Custom range start date"
                                />
                            </div>
                            <div className="schedule-date-toolbar__custom-field">
                                <label className="schedule-date-toolbar__custom-label" htmlFor="schedule-custom-to">
                                    To
                                </label>
                                <DatePicker
                                    id="schedule-custom-to"
                                    value={customEnd}
                                    onChange={onCustomEndChange}
                                    format="DD-MM-YYYY"
                                    className="schedule-date-toolbar__custom-picker"
                                    autoOk
                                    clearable
                                    minDate={customStart || undefined}
                                    aria-label="Custom range end date"
                                />
                            </div>
                            <Button
                                className="schedule-date-toolbar__apply"
                                onClick={onApplyCustom}
                                disabled={!canApplyCustom}
                            >
                                Apply
                            </Button>
                        </div>
                        {customError && (
                            <p className="schedule-date-toolbar__error" role="alert">
                                {customError}
                            </p>
                        )}
                        {startDate && endDate && (
                            <p className="schedule-date-toolbar__applied" role="status">
                                Showing {startDate.format("D MMM YYYY")} – {endDate.format("D MMM YYYY")}
                            </p>
                        )}
                    </div>
                )}
            </div>
        </MuiPickersUtilsProvider>
    );
};

ScheduleDateToolbar.propTypes = {
    mode: PropTypes.oneOf(["month", "week", "day", "custom"]),
    anchorDate: PropTypes.object,
    startDate: PropTypes.object,
    endDate: PropTypes.object,
    customStart: PropTypes.object,
    customEnd: PropTypes.object,
    onModeChange: PropTypes.func.isRequired,
    onNavigate: PropTypes.func.isRequired,
    onAnchorChange: PropTypes.func.isRequired,
    onReturnToCurrent: PropTypes.func.isRequired,
    onCustomStartChange: PropTypes.func.isRequired,
    onCustomEndChange: PropTypes.func.isRequired,
    onApplyCustom: PropTypes.func.isRequired,
    customError: PropTypes.string,
    canApplyCustom: PropTypes.bool,
    dayOfWeek: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
    onDayOfWeekChange: PropTypes.func.isRequired,
    showDayFilter: PropTypes.bool,
    loading: PropTypes.bool
};

ScheduleDateToolbar.defaultProps = {
    mode: "month",
    anchorDate: null,
    startDate: null,
    endDate: null,
    customStart: null,
    customEnd: null,
    customError: "",
    canApplyCustom: false,
    dayOfWeek: null,
    showDayFilter: false,
    loading: false
};

export default ScheduleDateToolbar;